import { Globe, Plus, Shield, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '@/OS/lib/utils';
import type { BrowserTabState } from './types';

type BrowserTabStripProps = {
  tabs: BrowserTabState[];
  activeTabId: string | null;
  privateMode: boolean;
  onActivate: (tabId: string) => void;
  onClose: (tabId: string) => void;
  onNewTab: () => void;
};

export const BrowserTabStrip = ({
  tabs,
  activeTabId,
  privateMode,
  onActivate,
  onClose,
  onNewTab,
}: BrowserTabStripProps) => {
  return (
    <div className="flex items-center gap-2 border-b border-border/70 bg-muted/35 px-2 py-2">
      {privateMode ? (
        <div className="flex shrink-0 items-center gap-2 rounded-md border border-primary/25 bg-primary/10 px-3 py-2 text-xs font-medium text-primary">
          <Shield className="h-3.5 w-3.5" />
          Private
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto pb-1">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onActivate(tab.id)}
              className={cn(
                'group flex min-w-[180px] max-w-[260px] items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                isActive
                  ? 'border-border bg-background text-foreground shadow-sm'
                  : 'border-transparent bg-background/60 text-muted-foreground hover:border-border/70 hover:text-foreground'
              )}
            >
              {tab.favicon ? (
                <img src={tab.favicon} alt="" className="h-4 w-4 shrink-0 rounded-sm" />
              ) : (
                <Globe className="h-4 w-4 shrink-0" />
              )}
              <span className="min-w-0 flex-1 truncate">
                {tab.title || 'New Tab'}
              </span>
              <span
                onClick={(event) => {
                  event.stopPropagation();
                  onClose(tab.id);
                }}
                className="rounded p-1 opacity-70 transition hover:bg-muted hover:opacity-100"
                aria-label={`Close ${tab.title || 'tab'}`}
                role="button"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            </button>
          );
        })}
      </div>

      <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={onNewTab}>
        <Plus className="mr-1.5 h-4 w-4" />
        {privateMode ? 'New Private Tab' : 'New Tab'}
      </Button>
    </div>
  );
};
